/**
 * Entity Documents Page
 * Sprint 2 / Sprint 11A: Updated with Layout component and Avida branding
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { RefreshCw, CheckCircle2, XCircle, FileText, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';

const EntityDocumentsPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  
  const [entityType, setEntityType] = useState('Company');
  const [entityId, setEntityId] = useState('COMPANY');
  const [completeness, setCompleteness] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!entityId.trim()) {
      toast.error('Please enter an entity ID');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      const completenessRes = await documentsAPI.completeness(entityType, entityId);
      const docsRes = await documentsAPI.list({ linked_entity_type: entityType, linked_entity_id: entityId });
      
      setCompleteness(completenessRes.data);
      setDocuments(docsRes.data.documents);
    } catch (err) {
      toast.error('Failed to fetch entity documents');
      setCompleteness(null);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleEntityTypeChange = (value) => {
    setEntityType(value);
    if (value === 'Company') {
      setEntityId('COMPANY');
    } else {
      setEntityId('');
    }
    setCompleteness(null);
    setDocuments([]);
    setSearched(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  const getStatusColor = (percent) => {
    if (percent >= 100) return 'text-green-600';
    if (percent >= 75) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <Layout title="Entity Documents">
      <div data-testid="entity-documents-page">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Select Entity</CardTitle>
            <CardDescription>Choose entity type and ID to view documents</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4 flex-wrap">
              <div className="w-48">
                <Label>Entity Type</Label>
                <Select value={entityType} onValueChange={handleEntityTypeChange}>
                  <SelectTrigger data-testid="entity-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Company">Company</SelectItem>
                    <SelectItem value="Client">Client</SelectItem>
                    <SelectItem value="Personnel">Personnel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label>Entity ID</Label>
                <Input 
                  value={entityId} 
                  onChange={(e) => setEntityId(e.target.value)}
                  placeholder="Enter entity ID..."
                  disabled={entityType === 'Company'}
                  data-testid="entity-id-input"
                />
              </div>
              <Button onClick={handleSearch} disabled={loading} data-testid="search-btn">
                {loading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                {!loading && <Search className="h-4 w-4 mr-2" />}
                Check Completeness
              </Button>
            </div>
          </CardContent>
        </Card>

        {searched && !loading && completeness && (
          <Card className="mb-6" data-testid="completeness-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {completeness.percent_complete >= 100 && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                    {completeness.percent_complete < 100 && completeness.percent_complete >= 75 && <AlertTriangle className="h-5 w-5 text-amber-600" />}
                    {completeness.percent_complete < 75 && <XCircle className="h-5 w-5 text-red-600" />}
                    Document Completeness
                  </CardTitle>
                  <CardDescription>{entityType}: {entityId}</CardDescription>
                </div>
                <div className="text-right">
                  <span className={`text-3xl font-bold ${getStatusColor(completeness.percent_complete)}`}>
                    {completeness.percent_complete}%
                  </span>
                  <p className="text-sm text-slate-500">
                    {completeness.required_types_present} of {completeness.total_required_types} required
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Progress value={completeness.percent_complete} className="h-3 mb-4" />
              
              {completeness.has_expired_documents && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <span className="text-amber-800">
                    Some documents have expired: {completeness.expired_document_codes.join(', ')}
                  </span>
                </div>
              )}
              
              {completeness.missing_required_types.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Missing Required Documents:</h4>
                  <div className="space-y-2">
                    {completeness.missing_required_types.map((mt, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="font-medium text-red-800">{mt.document_type_name}</span>
                        </div>
                        <Badge variant="outline" className="bg-red-100 text-red-700">{mt.required_trigger}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {completeness.percent_complete >= 100 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-green-800">All required documents are present and valid!</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {searched && !loading && (
          <Card data-testid="entity-docs-list">
            <CardHeader>
              <CardTitle>Uploaded Documents ({documents.length})</CardTitle>
              <CardDescription>All documents for this entity</CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                  <p>No documents uploaded for this entity</p>
                </div>
              )}
              {documents.length > 0 && (
                <div className="space-y-3">
                  {documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="font-medium text-slate-900">{doc.document_type_name || doc.document_type_code}</p>
                          <p className="text-sm text-slate-500">{doc.file_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">v{doc.version}</Badge>
                        {doc.status === 'Active' && !doc.is_expired && (
                          <Badge variant="outline" className="bg-green-100 text-green-700">Active</Badge>
                        )}
                        {doc.status === 'Active' && doc.is_expired && (
                          <Badge variant="outline" className="bg-red-100 text-red-700">Expired</Badge>
                        )}
                        {doc.status !== 'Active' && (
                          <Badge variant="outline" className="bg-slate-100 text-slate-600">Archived</Badge>
                        )}
                        <span className="text-sm text-slate-500">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {!searched && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8 text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p>Select an entity type and ID, then click Check Completeness</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default EntityDocumentsPage;
